import { cn } from "@core/lib/utils";
import { useCurrentCart } from "@ecom/hooks/cartHooks";
import { useCurrencies } from "@phoenix/products/hooks/useCurrencies";
import { Link, LinkProps } from "@remix-run/react";
import { IslandPreloadContext } from "@core/utils/island-preload-registry";
import { useStagedOrders } from "@/overrides/ecommerce/hooks/useStagedOrders";
import { formatMoney } from "@/overrides/ecommerce/routes/_site.cart.route/stagedOrderHelpers";

interface MiniCartProps extends React.PropsWithChildren<Omit<LinkProps, "to">> {
  type?: string;
  name?: string;
}

export const MicroCart: React.FC<MiniCartProps> = ({
  type = "ShoppingCart",
  name,
  className,
  ...props
}: MiniCartProps) => {
  const { cartQuery, updateItems, removeItems } = useCurrentCart(type, name);
  const currencies = useCurrencies();
  const productItems = cartQuery.data?.SalesItems ?? [];
  const totalItems = productItems.reduce((total: number, item: any) => total + item.Quantity, 0);
  const { inbox: pendingOrders, inboxTotal: pendingTotal } = useStagedOrders();
  const pendingCount = pendingOrders.length;
  const pendingAllSame = pendingCount > 1 && pendingOrders.every(o => o.Total === pendingOrders[0].Total);

  const setQty = (productId: number, quantity: number) => {
    if (quantity <= 0) {
      removeItems.mutate([productId]);
      return;
    }
    updateItems.mutate([{ ProductId: productId, Quantity: quantity }]);
  };

  return (
    <div className={cn("cart-icon-wrapper text-nowrap", className)} data-testid="micro-cart" data-cart-trigger>
      <Link to={`/cart/${type}`} {...props} className="block">
        <span className="sr-only">View Cart</span>
        <svg
          className="cart-icon-svg"
          viewBox="0 0 28 28"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M1 1H5L7.68 17.39C7.77 17.93 8.05 18.42 8.46 18.78C8.88 19.14 9.41 19.33 9.96 19.33H22.04C22.59 19.33 23.12 19.14 23.54 18.78C23.95 18.42 24.23 17.93 24.32 17.39L26 7H6"
            stroke="#ffffff"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <circle cx="10.5" cy="24.5" r="2" stroke="#ffffff" strokeWidth="1.8" />
          <circle cx="21.5" cy="24.5" r="2" stroke="#ffffff" strokeWidth="1.8" />
        </svg>
        <span
          className="cart-badge"
          id="cart-badge"
          data-testid="micro-cart-badge"
          style={{ display: totalItems === 0 && pendingCount > 0 ? "none" : "flex" }}
        >
          {totalItems}
        </span>
        {pendingCount > 0 && (
          <span className="cart-badge-pending" data-testid="micro-cart-pending-badge">
            {pendingCount}
          </span>
        )}
      </Link>

      <div className="mini-cart-dropdown hidden md:block">
        <div className="mini-cart-header">Shopping Cart</div>
        {pendingCount > 0 && (
          <div className="mini-cart-pending" data-testid="micro-cart-pending">
            <div className="mini-cart-pending-row">
              <span>Awaiting your signature ({pendingCount})</span>
              <span>{formatMoney(pendingTotal)}</span>
            </div>
            <div className="mini-cart-pending-detail">
              Pending total: {pendingCount} {pendingCount === 1 ? "order" : "orders"}
              {pendingAllSame ? ` × ${formatMoney(pendingOrders[0].Total)}` : ""} (subtotal plus shipping)
            </div>
            <Link to="/dashboard/pending-orders" className="mini-cart-pending-btn">
              Review pending orders
            </Link>
          </div>
        )}
        <div className="mini-cart-items">
          {productItems.length === 0 ? (
            pendingCount === 0 && <div className="mini-cart-empty">Your cart is empty</div>
          ) : (
            productItems.map((item: any) => (
              <div key={item.Id} className="mini-cart-item">
                <button
                  type="button"
                  className="mini-cart-remove"
                  aria-label="Remove item"
                  onClick={() => removeItems.mutate([item.ProductId])}
                >
                  ×
                </button>
                <div className="mini-cart-item-info">
                  <Link
                    to={`/product/${item.ProductSEOURL}`}
                    className="mini-cart-item-name block"
                    title={item.ProductName}
                  >
                    {item.ProductName}
                  </Link>
                  <div className="mini-cart-qty-row">
                    <button
                      type="button"
                      className="mini-cart-qty-btn"
                      aria-label="Decrease quantity"
                      onClick={() => setQty(item.ProductId, item.Quantity - 1)}
                    >
                      −
                    </button>
                    <span className="mini-cart-qty-val">{item.Quantity}</span>
                    <button
                      type="button"
                      className="mini-cart-qty-btn"
                      aria-label="Increase quantity"
                      onClick={() => setQty(item.ProductId, item.Quantity + 1)}
                    >
                      +
                    </button>
                  </div>
                </div>
                <div className="mini-cart-item-price">
                  {currencies.format(item.Amount)}
                </div>
              </div>
            ))
          )}
        </div>
        <div className="mini-cart-footer">
          <Link to={`/cart/${type}`} className="mini-cart-browse">
            View Cart
          </Link>
        </div>
      </div>
    </div>
  );
};

export default MicroCart;

export const islandPreload = async (
  _ctx: IslandPreloadContext,
  _props?: Record<string, unknown>
) => {
  return {};
};
